import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { progressItems, type ProgressItem } from '../lib/progress.js';
import { Button } from './Button.js';

function ProgressLabel({
  item,
  index,
  currentIndex,
}: {
  item: ProgressItem;
  index: number;
  currentIndex: number;
}) {
  const reached = index <= currentIndex;
  const active = index === currentIndex;
  return (
    <>
      <span
        aria-hidden="true"
        className={`flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold sm:text-[11px] sm:font-semibold ${
          reached ? 'bg-progress text-white' : 'bg-gray-100 text-gray-400'
        } ${active ? 'ring-2 ring-brand-soft ring-offset-2' : ''}`}
      >
        {index < currentIndex ? (
          <svg
            className="size-[9px] sm:size-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        ) : (
          index + 1
        )}
      </span>
      <span
        aria-hidden="true"
        className={`hidden text-center text-[10px] font-medium whitespace-nowrap sm:block ${
          active ? 'text-progress' : reached ? 'text-gray-500' : 'text-gray-300'
        }`}
      >
        {item.label}
      </span>
    </>
  );
}

export function StepProgress({
  currentIndex,
  disabled = false,
}: {
  currentIndex: number;
  disabled?: boolean;
}) {
  const current = progressItems[currentIndex];

  return (
    <nav aria-label="Planner progress" className="border-b border-gray-200 bg-white">
      <div className="mx-auto max-w-5xl px-4 py-3">
        <div className="mb-2 sm:hidden">
          <p className="text-xs font-medium tracking-widest text-gray-500 uppercase">
            {current.id === 'cart' ? 'Your order' : `Step ${currentIndex + 1} of 5`}
          </p>
          <p className="text-sm font-semibold text-gray-800">{current.label}</p>
        </div>
        <ol className="flex items-start">
          {progressItems.map((item, index) => {
            const classes =
              'flex min-h-11 min-w-11 flex-col items-center gap-1 rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';
            return (
              <li
                key={item.id}
                className={`flex min-w-0 items-start ${index < progressItems.length - 1 ? 'flex-1' : 'flex-none'}`}
              >
                {index <= currentIndex && !disabled ? (
                  <Link
                    to={item.to}
                    data-name={`step-link-${item.id}`}
                    aria-label={item.label}
                    aria-current={index === currentIndex ? 'page' : undefined}
                    className={classes}
                  >
                    <ProgressLabel item={item} index={index} currentIndex={currentIndex} />
                  </Link>
                ) : (
                  <button
                    type="button"
                    disabled
                    data-name={`step-link-${item.id}`}
                    aria-label={item.label}
                    className={classes}
                  >
                    <ProgressLabel item={item} index={index} currentIndex={currentIndex} />
                  </button>
                )}
                {index < progressItems.length - 1 && (
                  <span
                    aria-hidden="true"
                    className={`mx-1 mt-3 h-px flex-1 ${index < currentIndex ? 'bg-progress' : 'bg-gray-200'}`}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}

export function StepNavigation({
  currentIndex,
  canContinue = true,
  submit = false,
  mobileSticky = false,
}: {
  currentIndex: number;
  /** False renders a disabled Continue, e.g. until the Step has a valid choice. */
  canContinue?: boolean;
  submit?: boolean;
  mobileSticky?: boolean;
}) {
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  useEffect(() => {
    const viewport = mobileSticky ? window.visualViewport : undefined;
    if (!viewport) {
      return;
    }
    const update = () => {
      const offset = window.innerHeight - viewport.height - viewport.offsetTop;
      setKeyboardOffset(offset > 60 ? Math.round(offset) : 0);
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, [mobileSticky]);

  const previous = progressItems[currentIndex - 1];
  const next = progressItems[currentIndex + 1];
  const backLabel = (
    <>
      <span aria-hidden="true" className="mr-1">
        &larr;
      </span>
      Back
    </>
  );
  const continueLabel = (
    <>
      Continue
      <span aria-hidden="true" className="ml-1">
        &rarr;
      </span>
    </>
  );

  return (
    <nav
      aria-label="Planner navigation"
      style={mobileSticky && keyboardOffset ? { bottom: keyboardOffset } : undefined}
      className={
        mobileSticky
          ? 'fixed inset-x-0 bottom-0 z-30 flex justify-between gap-4 border-t border-gray-200 bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:static lg:mt-2 lg:border-0 lg:bg-transparent lg:p-0'
          : 'mt-12 flex justify-between gap-4'
      }
    >
      {previous ? (
        <Button asChild variant="secondary" className="rounded-xl">
          <Link to={previous.to} data-name="step-back">
            {backLabel}
          </Link>
        </Button>
      ) : (
        <Button variant="secondary" disabled data-name="step-back" className="rounded-xl">
          {backLabel}
        </Button>
      )}
      {next && submit ? (
        <Button
          type="submit"
          variant={mobileSticky ? 'progress' : 'primary-white'}
          disabled={!canContinue}
          data-name="step-continue"
          className="rounded-xl"
        >
          {continueLabel}
        </Button>
      ) : next && canContinue ? (
        <Button
          asChild
          variant={mobileSticky ? 'progress' : 'primary-white'}
          className="rounded-xl"
        >
          <Link to={next.to} data-name="step-continue">
            {continueLabel}
          </Link>
        </Button>
      ) : (
        <Button
          variant={mobileSticky ? 'progress' : 'primary-white'}
          disabled
          data-name="step-continue"
          className="rounded-xl"
        >
          {continueLabel}
        </Button>
      )}
    </nav>
  );
}
